import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth, type Profile } from "@/contexts/AuthContext";

/**
 * Page de garde du module /publi-temp.
 *
 * Module temporaire pour un admin externe : il doit pouvoir créer des biens
 * et les publier sur le site web, sans avoir accès au reste de l app.
 * Gate très basique : 1 login + 1 password en clair dans le bundle JS.
 * C est une protection contre l accès accidentel, pas un vrai secret.
 *
 * Sur succès : on charge un profile admin via le RPC dev SECURITY DEFINER
 * (get_profiles_for_role), on appelle enterDevSession pour authentifier le
 * front au rôle admin, on flag localStorage pour que le ProtectedRoute laisse
 * passer, et on redirige sur la liste.
 */

const TEMP_LOGIN = import.meta.env.VITE_PUBLI_TEMP_LOGIN ?? "";
const TEMP_PWD = import.meta.env.VITE_PUBLI_TEMP_PWD ?? "";
export const PUBLI_TEMP_GATE_KEY = "publi_temp_gate";

function isGateOpen(): boolean {
  try {
    return sessionStorage.getItem(PUBLI_TEMP_GATE_KEY) === "1";
  } catch {
    return false;
  }
}

export function openGate() {
  sessionStorage.setItem(PUBLI_TEMP_GATE_KEY, "1");
}

export function closeGate() {
  sessionStorage.removeItem(PUBLI_TEMP_GATE_KEY);
}

export function TempPublishLoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, profile, enterDevSession } = useAuth();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Déjà passé la garde + déjà en session admin → direct la liste.
  if (isGateOpen() && isAuthenticated && profile?.role === "admin") {
    return <Navigate to="/publi-temp" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!TEMP_LOGIN || !TEMP_PWD) {
      setError("Module non configuré. Contactez l'administrateur.");
      return;
    }

    if (login.trim() !== TEMP_LOGIN || password !== TEMP_PWD) {
      setError("Login ou mot de passe incorrect.");
      return;
    }

    setLoading(true);
    try {
      // Récupère un profile admin pour l'attacher à la dev session. Le RPC
      // est SECURITY DEFINER (mig 031) et accepte un appel anon, c'est ce
      // que /login/select-user utilise déjà côté front.
      const { data, error: rpcError } = await supabase.rpc(
        "get_profiles_for_role",
        { p_role: "admin" },
      );
      if (rpcError) throw new Error(rpcError.message);
      const profiles = (data ?? []) as Profile[];
      const adminProfile = profiles.find((p) => p.role === "admin");
      if (!adminProfile) {
        throw new Error("Aucun profil admin disponible.");
      }
      // ORDRE IMPORTANT : enterDevSession nettoie sessionStorage (sauf sa propre
      // clé). On ouvre le gate APRÈS pour que le flag survive au cleanup.
      enterDevSession(adminProfile);
      openGate();
      navigate("/publi-temp", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur de connexion");
      setLoading(false);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6 flex flex-col gap-4 max-w-md w-full">
      <header className="flex items-center gap-2">
        <Lock size={16} className="text-accent" />
        <h1 className="text-sm font-medium text-text-primary uppercase tracking-wider">
          Publication Skyline Renting
        </h1>
      </header>
      <p className="text-xs text-text-muted">
        Accès réservé. Saisissez vos identifiants pour gérer la publication des biens.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wider text-text-muted">
            Login
          </span>
          <input
            type="text"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            autoComplete="username"
            disabled={loading}
            className="px-3 py-2 rounded-md border border-border bg-surface text-sm text-text-primary outline-none focus:border-accent"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wider text-text-muted">
            Mot de passe
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={loading}
            className="px-3 py-2 rounded-md border border-border bg-surface text-sm text-text-primary outline-none focus:border-accent"
          />
        </label>
        {error && (
          <p className="text-xs text-danger px-1">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="mt-2 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : null}
          Se connecter
        </button>
      </form>
    </div>
  );
}
